import React, { useState } from 'react';
import { ClienteConsolidado } from '../types';
import { formatCurrency } from '../utils';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';

interface Props {
  clientes: ClienteConsolidado[];
  onNavigate?: (tab: string, clientName?: string) => void;
}

const MatrizRisco: React.FC<Props> = ({ clientes, onNavigate }) => {
  const [expandedQuad, setExpandedQuad] = useState<string | null>(null);

  const internos = clientes.filter(c => c.interno);
  const externos = clientes.filter(c => !c.interno);

  const getGroup = (c: ClienteConsolidado) => {
    const altoValor = c.valorTotal > 5000;
    const altoSaldo = c.saldoAcumulado > 5000;
    if (altoValor && altoSaldo) return { quad: 'Q1', desc: 'RISCO ESTRATÉGICO', bg: '#fef2f2', color: '#ef4444' };
    if (altoValor && !altoSaldo) return { quad: 'Q2', desc: 'RISCO ALTO (Sem Saldo)', bg: '#fffbeb', color: '#f59e0b' };
    if (!altoValor && altoSaldo) return { quad: 'Q3', desc: 'RISCO MÉDIO (Com Saldo)', bg: '#eff6ff', color: '#3b82f6' };
    return { quad: 'Q4', desc: 'RISCO BAIXO', bg: '#f0fdf4', color: '#10b981' };
  };

  const agrupado = externos.reduce((acc, c) => {
    const g = getGroup(c).quad;
    if (!acc[g]) acc[g] = { valor: 0, qtd: 0, clientes: [] };
    acc[g].valor += c.valorTotal;
    acc[g].qtd += 1;
    acc[g].clientes.push(c);
    return acc;
  }, {} as any);

  const renderQuad = (q: string, info: any) => {
    const val = agrupado[q]?.valor || 0;
    const qtd = agrupado[q]?.qtd || 0;
    const allClientes = (agrupado[q]?.clientes || []).sort((a: any, b: any) => b.valorTotal - a.valorTotal);
    const isExpanded = expandedQuad === q;
    const clientesToShow = isExpanded ? allClientes : allClientes.slice(0, 5);

    // Prepare chart data (Score distribution)
    const scoreMap = allClientes.reduce((acc: any, c: any) => {
      acc[c.score] = (acc[c.score] || 0) + 1;
      return acc;
    }, {});
    const chartData = ['E', 'D', 'C', 'B', 'A'].map(s => ({
      score: s,
      qtd: scoreMap[s] || 0
    })).filter(d => d.qtd > 0);

    return (
      <div className="rounded-2xl p-6 border shadow-sm flex flex-col transition-all bg-white relative overflow-hidden h-fit" style={{ borderColor: info.color + '40' }}>
        <div className="absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-10 pointer-events-none" style={{ backgroundColor: info.color }}></div>
        
        <div className="mb-2 relative z-10">
          <h3 className="font-black text-sm mb-1 uppercase tracking-wide" style={{ color: info.color }}>{info.desc}</h3>
          <p className="text-[11px] text-gray-500 font-medium">{info.detail}</p>
        </div>
        
        <div className="mb-5 mt-2 relative z-10">
          <p className="text-3xl font-black tracking-tight text-gray-800">{formatCurrency(val)}</p>
          <p className="text-xs font-semibold mt-1 text-gray-400">{qtd} clientes identificados neste perfil</p>
        </div>
        
        {chartData.length > 0 && (
          <div className="mb-5 bg-gray-50/50 rounded-xl p-3 border border-gray-100 relative z-10">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 ml-2">Distribuição por Score</p>
            <div className="h-28 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                  <XAxis dataKey="score" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 'bold' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <Tooltip 
                    cursor={{fill: 'rgba(0,0,0,0.05)'}}
                    contentStyle={{ borderRadius: '8px', fontSize: '11px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="qtd" name="Nº de Clientes" radius={[4, 4, 0, 0]} barSize={32}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={info.color} opacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-[9px] text-gray-400 flex flex-wrap gap-2.5 justify-center bg-white py-1.5 px-2 rounded-lg border border-gray-100 shadow-sm">
              <span className="font-medium"><strong className="text-gray-500">A:</strong> 1 a 15d</span>
              <span className="font-medium"><strong className="text-gray-500">B:</strong> 16 a 30d</span>
              <span className="font-medium"><strong className="text-gray-500">C:</strong> 31 a 60d</span>
              <span className="font-medium"><strong className="text-gray-500">D:</strong> 61 a 90d</span>
              <span className="font-medium"><strong className="text-gray-500">E:</strong> +90 dias</span>
            </div>
          </div>
        )}

        {clientesToShow.length > 0 && (
          <div className="pt-4 border-t relative z-10" style={{ borderColor: info.color + '20' }}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Principais Devedores</p>
            <ul className="space-y-2.5">
              {clientesToShow.map((c: any) => (
                <li key={c.nome} className="flex justify-between items-center text-xs group cursor-pointer" onClick={() => onNavigate && onNavigate('perfil', c.nome)}>
                  <span className="font-bold text-gray-600 truncate mr-3 group-hover:text-orange-500 group-hover:underline transition-all">{c.nome}</span>
                  <span className="font-black text-gray-800 bg-gray-50 px-2 py-0.5 rounded">{formatCurrency(c.valorTotal)}</span>
                </li>
              ))}
              {!isExpanded && qtd > 5 && (
                <li 
                  className="text-xs text-gray-500 font-bold mt-3 cursor-pointer hover:bg-gray-100 transition-colors text-center py-2 bg-gray-50 rounded-lg border border-gray-100"
                  onClick={() => setExpandedQuad(q)}
                >
                  Ver mais {qtd - 5} clientes
                </li>
              )}
              {isExpanded && (
                <li 
                  className="text-xs text-gray-500 font-bold mt-3 cursor-pointer hover:bg-gray-100 transition-colors text-center py-2 bg-gray-50 rounded-lg border border-gray-100"
                  onClick={() => setExpandedQuad(null)}
                >
                  Ocultar lista expandida
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Matriz Inteligente de Risco</h2>
        <p className="text-sm text-gray-500">Cruzamento de Valor em Aberto x Saldo Acumulado (Energia Retida)</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {renderQuad('Q1', { desc: '🔴 RISCO ESTRATÉGICO', bg: '#fef2f2', color: '#ef4444', detail: 'Alto Débito + Alto Saldo. Foco em Distrato.' })}
        {renderQuad('Q2', { desc: '⚠️ RISCO ALTO', bg: '#fffbeb', color: '#f59e0b', detail: 'Alto Débito + Baixo Saldo. Foco em Cobrança Ativa/Judicial.' })}
        {renderQuad('Q3', { desc: '🔵 RISCO MÉDIO', bg: '#eff6ff', color: '#3b82f6', detail: 'Baixo Débito + Alto Saldo. Fácil recuperação com compensação.' })}
        {renderQuad('Q4', { desc: '🟢 RISCO BAIXO', bg: '#f0fdf4', color: '#10b981', detail: 'Baixo Débito + Baixo Saldo. Régua de cobrança automatizada.' })}
      </div>
    </div>
  );
};

export default MatrizRisco;
